/**
 * API Service for BookingHall Platform
 * Handles all HTTP requests to the Django backend
 */
import axios from 'axios';

// Base URL - can be configured via environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');

        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        // Try to refresh the token
        const response = await axios.post(`${API_BASE_URL}/auth/refresh/`, {
          refresh: refreshToken,
        });

        const { access } = response.data;

        // Save new access token
        localStorage.setItem('access_token', access);

        // Update default header
        api.defaults.headers.common['Authorization'] = `Bearer ${access}`;

        // Retry original request
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear tokens and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');

        window.location.href = '/admin-login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// PUBLIC APIS
// ============================================

export const hallAPI = {
  /**
   * Get all active halls
   */
  getAll: () => api.get('/halls/'),

  /**
   * Get hall details by slug
   */
  getBySlug: (slug) => api.get(`/halls/${slug}/`),

  /**
   * Get hall availability for a month
   * Optional floorId parameter for multi-floor halls
   */
  getAvailability: (slug, year, month, floorId = null) => {
    const params = { year, month };
    if (floorId) {
      params.floor_id = floorId;
    }
    return api.get(`/halls/${slug}/availability/`, { params });
  },

  /**
   * Get hall booking calendar
   */
  getCalendar: (slug) => api.get(`/halls/${slug}/calendar/`),

  /**
   * Check if hall is available on a specific date
   */
  checkAvailability: (hallSlug, date) =>
    api.post('/check-availability/', { hall_slug: hallSlug, date }),
};

// ============================================
// AUTHENTICATION APIS
// ============================================

export const authAPI = {
  /**
   * Login - get access and refresh tokens
   */
  login: (username, password) =>
    api.post('/auth/login/', { username, password }),

  /**
   * Refresh access token
   */
  refresh: (refreshToken) =>
    api.post('/auth/refresh/', { refresh: refreshToken }),

  /**
   * Logout - clear local storage
   */
  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
  },
};

// ============================================
// HALL ADMIN APIS
// ============================================

export const adminAPI = {
  /**
   * Get dashboard statistics
   */
  getDashboard: () => api.get('/admin/dashboard/'),

  /**
   * Get admin's hall details
   */
  getHall: () => api.get('/admin/hall/'),

  /**
   * Update hall details
   */
  updateHall: (data) => api.put('/admin/hall/', data),

  /**
   * Upload hall image
   */
  uploadImage: (formData) =>
    api.post('/admin/hall/upload-image/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Booking management
  /**
   * Get all bookings for admin's hall
   */
  getBookings: (params) => api.get('/admin/bookings/', { params }),

  /**
   * Get booking details
   */
  getBooking: (id) => api.get(`/admin/bookings/${id}/`),

  /**
   * Create new booking
   */
  createBooking: (data) => api.post('/admin/bookings/', data),

  /**
   * Update booking
   */
  updateBooking: (id, data) => api.put(`/admin/bookings/${id}/`, data),

  /**
   * Cancel booking
   */
  cancelBooking: (id, reason) =>
    api.post(`/admin/bookings/${id}/cancel/`, { reason }),

  /**
   * Add payment to booking
   */
  addPayment: (bookingId, data) =>
    api.post(`/admin/bookings/${bookingId}/add_payment/`, data),

  // Reports
  /**
   * Get booking reports
   */
  getBookingReports: (params) =>
    api.get('/admin/reports/bookings/', { params }),

  /**
   * Get revenue reports
   */
  getRevenueReports: (params) =>
    api.get('/admin/reports/revenue/', { params }),
};

// ============================================
// SUPER ADMIN APIS
// ============================================

export const superAdminAPI = {
  /**
   * Get platform-wide dashboard
   */
  getDashboard: () => api.get('/superadmin/dashboard/'),

  /**
   * Get all halls
   */
  getHalls: () => api.get('/superadmin/halls/'),

  /**
   * Create new hall
   */
  createHall: (data) => api.post('/superadmin/halls/', data),

  /**
   * Update hall
   */
  updateHall: (id, data) => api.put(`/superadmin/halls/${id}/`, data),

  /**
   * Delete hall
   */
  deleteHall: (id) => api.delete(`/superadmin/halls/${id}/`),

  /**
   * Create hall admin
   */
  createHallAdmin: (hallId, data) =>
    api.post(`/superadmin/halls/${hallId}/admin/`, data),

  /**
   * Get all bookings across all halls
   */
  getAllBookings: (params) => api.get('/superadmin/bookings/', { params }),

  /**
   * Get platform revenue
   */
  getRevenue: (params) => api.get('/superadmin/revenue/', { params }),

  /**
   * Get analytics
   */
  getAnalytics: (params) => api.get('/superadmin/analytics/', { params }),
};

// Export default api instance
export default api;
